import { useBuilderContext } from "@Context/game/builder/builder-context";
import "@pixi/events";
import { Graphics as PixiGraphics } from "@pixi/graphics";
import { Container, Graphics } from "@pixi/react";
import { useCallback, useEffect, useState } from "react";
import { Waypoint as WaypointType } from "./models/builder-data-types";
import Waypoint from "./waypoint";

const Route = (props: {
  points: WaypointType[];
  count?: number;
  selected?: boolean;
  color?: string;
  alphaScale?: number;
  temporalAlpha?: boolean;
  onChange?: (points: WaypointType[]) => void;
}) => {
  const { state, dispatch } = useBuilderContext();

  const [points, setPoints] = useState<WaypointType[]>(props.points || []);
  const [selected, setSelected] = useState(props.selected || false);
  const [selectedWaypoint, setSelectedWaypoint] = useState(-1);

  const alphaScale = props.alphaScale || 5;
  const temporalAlpha = props.temporalAlpha || false;

  useEffect(() => {
    const newPoints: WaypointType[] = [];
    for (let i = 0; i < props.points.length; i++) {
      newPoints.push({ ...props.points[i] });
    }
    setPoints(newPoints);
  }, [props.points]);

  useEffect(() => {
    setSelected(props.selected || false);
  }, [props.selected]);

  const draw = useCallback(
    (g: PixiGraphics) => {
      g.clear();
      points.forEach((point, index) => {
        const midT = index === 0 ? 0 : (points[index - 1].t + point.t) / 2;
        const timeDiff = Math.min(alphaScale, Math.abs(state.time - midT)) / alphaScale;
        const alpha = temporalAlpha ? Math.max(0, 1 - timeDiff) : 1;
        g.lineStyle(4.5 * state.spriteScale, props.color || 0xffffff, selected ? 1 : 0.5 * alpha);
        if (index === 0) g.moveTo(point.position.x, point.position.y);
        else g.bezierCurveTo(points[index - 1].next.x, points[index - 1].next.y, point.prev.x, point.prev.y, point.position.x, point.position.y);
      });
    },
    [points, props.color, props.alphaScale, props.temporalAlpha, selected, state.time, state.spriteScale]
  );

  const updatePoint = (index: number, point: WaypointType) => {
    const newPoints = [];
    for (let i = 0; i < points.length; i++) {
      newPoints.push({ ...points[i] });
    }
    newPoints[index] = point;
    setPoints(newPoints);
    if (props.onChange) props.onChange(newPoints);
  };

  const handleDragStart = (index: number) => {
    setSelectedWaypoint(index);
  };

  useEffect(() => {
    if (selected) setSelectedWaypoint(state.selectedWaypoint);
  }, [state.selectedWaypoint]);

  return (
    <Container>
      <Graphics draw={draw} />
      {selected &&
        state.options.showWaypoints &&
        points.map((point, index) => (
          <Waypoint
            index={index}
            selected={selected && index === selectedWaypoint}
            routeSelected={selected}
            hasNext={points.length > 1 && index < points.length - 1}
            position={point.position}
            prev={point.prev}
            next={point.next}
            color={props.color}
            time={point.t}
            onChange={(p) => {
              updatePoint(index, p);
            }}
            onDragStart={() => {
              handleDragStart(index);
            }}
          />
        ))}
    </Container>
  );
};

export default Route;
